/**
 * Flatten the simulate/data-panel `hierarchy` tree into the four lookup
 * Maps the aggregated tabs and the SUMMARY view read
 * (categories / subcategories / suppliers / brands, each keyed by id,
 * plus a `noproduct*` array copy of each).
 *
 * Extracted VERBATIM from `EditSimulateCategory.extractHierarchyData`
 * (which now delegates here) so the AUI screen can build the same
 * structure when the v3 canvas replaces EditSimulateCategory: v3 renders
 * its own canvas and never runs the legacy data-panel enrichment, so
 * MPsimulateAllCategory feeds the Summary tab from the v3 contract's
 * `hierarchyAnalytics` through `hierarchyFromAnalytics` below. One
 * implementation, two callers (NFR-12: no V2/Old duplicate variants).
 *
 * Pure: no `this`, no state, no side effects.
 */
export const extractHierarchyData = (hierarchy) => {
    if (!hierarchy || !hierarchy.categories) return null;

    const categoryMap = new Map();
    const subcategoryMap = new Map(); 
    const supplierMap = new Map();
    const brandMap = new Map();

    // Iterate through hierarchy to build lookup maps
    hierarchy.categories.forEach(category => {
        // Store category data using categoryId as key
        categoryMap.set(category.categoryId, {
            ...category,
            lambdaData: {
                // Category data mapping based on actual hierarchy structure
                allocatedShareCategory: category.allocatedShareCategory,
                weightedShareCategory: category.weightedShareCategory,
                avgPricePerProduct: category.avgPricePerProduct,
                salesUnits: category.salesUnits,
                salesCurrency: category.salesCurrency,
                profitCurrency: category.profitCurrency,
                profitabilityPercent: category.profitabilityPercent,
                dailySalesRateCurrency: category.dailySalesRateCurrency,
                dailySalesRateUnits: category.dailySalesRateUnits,
                // Hierarchical context for category level
                categoryName: category.categoryName,
                categoryId: category.categoryId,
                categoryCode: category.posCode,
                departmentId: hierarchy.departmentId, // From parent hierarchy
                barcodeCount: category.barcodeCount || category.dataPanelData?.barcodeCountAll
            },
            normalizedLambdaData: { ...(category.normalizedData || {}), ...(category.dataPanelData?.normalized || {}) },
            dataPanelData: {
                ...category.dataPanelData,
                actualSharePlanogram: category.dataPanelData?.actualSharePlanogram,
                weightedSharePlanogram: category.dataPanelData?.weightedSharePlanogram,

                totalAllocatedWidth: category.dataPanelData?.totalAllocatedWidth,
                shelfCapacity: category.dataPanelData?.shelfCapacity,
                salesPerFacing: category.dataPanelData?.salesPerFacing,
                totalFacing: category.dataPanelData?.totalFacing,
                unitsPerFacing: category.dataPanelData?.unitsPerFacing,
                salesPerMeter: category.dataPanelData?.salesPerMeter,
                unitsPerMeter: category.dataPanelData?.unitsPerMeter,
                shareOfSaleCurrency: category.dataPanelData?.shareOfSaleCurrency,
                shareOfSaleUnit: category.dataPanelData?.shareOfSaleUnit,
                shareOfSaleProfit: category.dataPanelData?.shareOfSaleProfit,
                // Keep legacy fields for fallback
                actualShare: category.allocatedShare || category.dataPanelData?.actualSharePlanogram,
                weightedShare: category.weightedShare || category.dataPanelData?.weightedSharePlanogram
            }
        });

        // Process subcategories
        if (category.subcategories) {
            category.subcategories.forEach(subcategory => {
                subcategoryMap.set(subcategory.subCategoryId, {
                    ...subcategory,
                    lambdaData: {
                        // Subcategory data mapping based on actual hierarchy structure
                        allocatedShareSubcategory: subcategory.allocatedShareSubcategory,
                        weightedShareSubcategory: subcategory.weightedShareSubcategory,
                        avgPricePerProduct: subcategory.avgPricePerProduct,
                        salesUnits: subcategory.salesUnits,
                        salesCurrency: subcategory.salesCurrency,
                        profitCurrency: subcategory.profitCurrency,
                        profitabilityPercent: subcategory.profitabilityPercent,
                        dailySalesRateCurrency: subcategory.dailySalesRateCurrency,
                        dailySalesRateUnits: subcategory.dailySalesRateUnits,
                        // Hierarchical context for subcategory level
                        subcategoryName: subcategory.subcategoryName,
                        subCategoryId: subcategory.subCategoryId,
                        subcategoryCode: subcategory.posCode,
                        categoryName: category.categoryName, // From parent category
                        categoryId: category.categoryId,     // From parent category
                        categoryCode: category.posCode, // From parent category
                        departmentId: hierarchy.departmentId, // From root hierarchy
                        barcodeCount: subcategory.barcodeCount || subcategory.dataPanelData?.barcodeCountAll
                    },
                    normalizedLambdaData: { ...(subcategory.normalizedData || {}), ...(subcategory.dataPanelData?.normalized || {}) },
                    dataPanelData: {
                        ...subcategory.dataPanelData,
                        actualSharePlanogram: subcategory.dataPanelData?.actualSharePlanogram,
                        weightedSharePlanogram: subcategory.dataPanelData?.weightedSharePlanogram,
                        actualShareCategory: subcategory.dataPanelData?.actualShareCategory,
                        weightedShareCategory: subcategory.dataPanelData?.weightedShareCategory,

                        totalAllocatedWidth: subcategory.dataPanelData?.totalAllocatedWidth,
                        shelfCapacity: subcategory.dataPanelData?.shelfCapacity,
                        salesPerFacing: subcategory.dataPanelData?.salesPerFacing,
                        totalFacing: subcategory.dataPanelData?.totalFacing,
                        unitsPerFacing: subcategory.dataPanelData?.unitsPerFacing,
                        salesPerMeter: subcategory.dataPanelData?.salesPerMeter,
                        unitsPerMeter: subcategory.dataPanelData?.unitsPerMeter,
                        shareOfSaleCurrency: subcategory.dataPanelData?.shareOfSaleCurrency,
                        shareOfSaleUnit: subcategory.dataPanelData?.shareOfSaleUnit,
                        shareOfSaleProfit: subcategory.dataPanelData?.shareOfSaleProfit,
                        // Keep legacy fields for fallback
                        actualShare: subcategory.allocatedShare || subcategory.dataPanelData?.actualSharePlanogram,
                        weightedShare: subcategory.weightedShare || subcategory.dataPanelData?.weightedSharePlanogram,
                    }
                });

                // Process suppliers
                if (subcategory.suppliers) {
                    subcategory.suppliers.forEach(supplier => {
                        // Create supplier key as supplierId+subCategoryId combination for uniqueness
                        const supplierKey = `${supplier.supplierId}-${subcategory.subCategoryId}`;
                        supplierMap.set(supplierKey, {
                            ...supplier,
                            lambdaData: {
                                // Supplier data mapping based on actual hierarchy structure
                                actualShareSupplier: supplier.actualShareSupplier,
                                allocatedShareSupplier: supplier.allocatedShareSupplier,
                                weightedShareSupplier: supplier.weightedShareSupplier,
                                avgPricePerProduct: supplier.avgPricePerProduct,
                                salesUnits: supplier.salesUnits,
                                salesCurrency: supplier.salesCurrency,
                                profitCurrency: supplier.profitCurrency,
                                profitabilityPercent: supplier.profitabilityPercent,
                                dailySalesRateCurrency: supplier.dailySalesRateCurrency,
                                dailySalesRateUnits: supplier.dailySalesRateUnits,
                                // Hierarchical context for supplier level
                                supplierName: supplier.supplierName,
                                supplierId: supplier.supplierId,
                                supplierCode: supplier.posCode,
                                subcategoryName: subcategory.subcategoryName, // From parent subcategory
                                subCategoryId: subcategory.subCategoryId,     // From parent subcategory
                                subcategoryCode: subcategory.subcategoryCode, // From parent subcategory
                                categoryName: category.categoryName,         // From grandparent category
                                categoryId: category.categoryId,             // From grandparent category
                                categoryCode: category.categoryCode,   // From grandparent category
                                departmentId: hierarchy.departmentId,        // From root hierarchy
                                barcodeCount: supplier.barcodeCount || supplier.dataPanelData?.barcodeCountAll
                            },
                            normalizedLambdaData: { ...(supplier.normalizedData || {}), ...(supplier.dataPanelData?.normalized || {}) },
                            dataPanelData: {
                                ...supplier.dataPanelData,

                                actualShareCategory: supplier.dataPanelData?.actualShareCategory,
                                weightedShareCategory: supplier.dataPanelData?.weightedShareCategory,
                                actualShareSubCategory: supplier.dataPanelData?.actualShareSubCategory || supplier.dataPanelData?.actualShareSubcategory,
                                weightedShareSubCategory: supplier.dataPanelData?.weightedShareSubCategory || supplier.dataPanelData?.weightedShareSubcategory,
                                actualSharePlanogram: supplier.dataPanelData?.actualSharePlanogram,
                                weightedSharePlanogram: supplier.dataPanelData?.weightedSharePlanogram,

                                totalAllocatedWidth: supplier.dataPanelData?.totalAllocatedWidth,
                                shelfCapacity: supplier.dataPanelData?.shelfCapacity,
                                salesPerFacing: supplier.dataPanelData?.salesPerFacing,
                                totalFacing: supplier.dataPanelData?.totalFacing,
                                unitsPerFacing: supplier.dataPanelData?.unitsPerFacing,
                                salesPerMeter: supplier.dataPanelData?.salesPerMeter,
                                unitsPerMeter: supplier.dataPanelData?.unitsPerMeter,
                                shareOfSaleCurrency: supplier.dataPanelData?.shareOfSaleCurrency,
                                shareOfSaleUnit: supplier.dataPanelData?.shareOfSaleUnit,
                                shareOfSaleProfit: supplier.dataPanelData?.shareOfSaleProfit,
                                // Keep legacy fields for fallback
                                actualShare: supplier.allocatedShare || supplier.dataPanelData?.actualSharePlanogram,
                                weightedShare: supplier.weightedShare || supplier.dataPanelData?.weightedSharePlanogram
                            }
                        });

                        // Process brands  
                        if (supplier.brands) {
                            supplier.brands.forEach(brand => {
                                // Create brand key as brandId+subCategoryId combination for uniqueness
                                const brandKey = `${brand.brandId}-${subcategory.subCategoryId}`;
                                brandMap.set(brandKey, {
                                    ...brand,
                                    subcategoryName: subcategory.subcategoryName,
                                    lambdaData: {
                                        // Brand data mapping based on actual hierarchy structure
                                        actualShareBrand: brand.actualShareBrand,
                                        allocatedShareBrand: brand.allocatedShareBrand,
                                        weightedShareBrand: brand.weightedShareBrand,
                                        avgPricePerProduct: brand.avgPricePerProduct,
                                        salesUnits: brand.salesUnits,
                                        salesCurrency: brand.salesCurrency,
                                        profitCurrency: brand.profitCurrency,
                                        profitabilityPercent: brand.profitabilityPercent,
                                        dailySalesRateCurrency: brand.dailySalesRateCurrency,
                                        dailySalesRateUnits: brand.dailySalesRateUnits,
                                        // Hierarchical context for brand level
                                        brandName: brand.brandName,
                                        brandId: brand.brandId,
                                        brandCode: brand.posCode,
                                        supplierName: supplier.supplierName,         // From parent supplier
                                        supplierId: supplier.supplierId,             // From parent supplier
                                        supplierCode: supplier.posCode,   // From parent supplier
                                        subcategoryName: subcategory.subcategoryName, // From grandparent subcategory
                                        subCategoryId: subcategory.subCategoryId,     // From grandparent subcategory
                                        subcategoryCode: subcategory.subcategoryCode, // From grandparent subcategory
                                        categoryName: category.categoryName,         // From great-grandparent category
                                        categoryId: category.categoryId,             // From great-grandparent category
                                        categoryCode: category.categoryCode,   // From great-grandparent category
                                        departmentId: hierarchy.departmentId,         // From root hierarchy
                                        barcodeCount: brand.barcodeCount || brand.dataPanelData?.barcodeCountAll
                                    },
                                    normalizedLambdaData: { ...(brand.normalizedData || {}), ...(brand.dataPanelData?.normalized || {}) },
                                    dataPanelData: {
                                        ...brand.dataPanelData,

                                        actualShareCategory: brand.dataPanelData?.actualShareCategory,
                                        weightedShareCategory: brand.dataPanelData?.weightedShareCategory,
                                        actualShareSubCategory: brand.dataPanelData?.actualShareSubCategory || brand.dataPanelData?.actualShareSubcategory,
                                        weightedShareSubCategory: brand.dataPanelData?.weightedShareSubCategory || brand.dataPanelData?.weightedShareSubcategory,
                                        actualSharePlanogram: brand.dataPanelData?.actualSharePlanogram,
                                        weightedSharePlanogram: brand.dataPanelData?.weightedSharePlanogram,

                                        totalAllocatedWidth: brand.dataPanelData?.totalAllocatedWidth,
                                        shelfCapacity: brand.dataPanelData?.shelfCapacity,
                                        salesPerFacing: brand.dataPanelData?.salesPerFacing,
                                        totalFacing: brand.dataPanelData?.totalFacing,
                                        unitsPerFacing: brand.dataPanelData?.unitsPerFacing,
                                        salesPerMeter: brand.dataPanelData?.salesPerMeter,
                                        unitsPerMeter: brand.dataPanelData?.unitsPerMeter,
                                        shareOfSaleCurrency: brand.dataPanelData?.shareOfSaleCurrency,
                                        shareOfSaleUnit: brand.dataPanelData?.shareOfSaleUnit,
                                        shareOfSaleProfit: brand.dataPanelData?.shareOfSaleProfit,
                                        // Keep legacy fields for fallback
                                        actualShare: brand.allocatedShare || brand.dataPanelData?.actualSharePlanogram,
                                        weightedShare: brand.weightedShare || brand.dataPanelData?.weightedSharePlanogram
                                    }
                                });
                            });
                        }
                    });
                }
            });
        }
    });

    return {
        categories: categoryMap,
        subcategories: subcategoryMap,
        suppliers: supplierMap,
        brands: brandMap,
        // Add noproduct datasets that contain ALL hierarchy items from endpoint
        noproductCategories: [...categoryMap.values()],
        noproductSubcategories: [...subcategoryMap.values()],
        noproductSuppliers: [...supplierMap.values()],
        noproductBrands: [...brandMap.values()]
    };
}


/**
 * The v3 contract carries the SAME tree as `hierarchy.categories` — the
 * bare categories ARRAY, under `hierarchyAnalytics`
 * (simulation-canonical-serializer-sup: `pick(hierarchy, "categories")`).
 * Wrap it back into the `{categories, departmentId}` shape
 * `extractHierarchyData` expects. Tolerates either form, since the
 * data-panel endpoint falls back to the whole hierarchy object when it
 * has no categories key.
 */
export const hierarchyFromAnalytics = (analytics, departmentId) => {
    if (!analytics) return null;
    const hierarchy = Array.isArray(analytics)
        ? { categories: analytics }
        : analytics;
    if (!hierarchy.categories) return null;
    return extractHierarchyData({ ...hierarchy, departmentId: departmentId ?? hierarchy.departmentId });
};
